import { getSessionId } from './getSessionId';
import { sanitizeGraphqlErrorMessage } from './sanitizeGraphqlErrorMessage';

const MAX_CUSTOM_VALUE_LENGTH = 2048;
const emailRegex = /([a-zA-Z0-9+_\.-]+)(@|%40)([\da-zA-Z\.-]+)\.([a-zA-Z\.]{2,6})/;
const userNameInUrlRegex = /((user\/)[a-zA-Z]*\/)/g;
const branchNameRegex = /((\?branch=)[a-zA-Z]*\-)/g;
const searchQueryRegex = /(\?|&)query=([^&]*)/i;
const accountInUrlRegex = /(\/account\/(.*?)\/(.*?)\/(.*))|(%2Faccount%2F(.*?)%2F(.*?)%2F(.*))/g;
const routingInUrlRegex = /[\/'"]((?:OID|PUID):[^\/'"]+)[\/'"]/i;
const tokenInUrlRegex =
    /(\?|&|#|%3F|%26|%23){1}((?:access_token|authToken|signature|password|secret|token|key|code|pwd|sig)(=|%3D)[^'"&\n]+)/gi;
const encodedTokenStart = 'eyJ'; // JWT token typically starts with "eyJ" which is a base64 encoded string for '{"'
const bearerTokenStart = 'Bearer '; // Bearer token typically starts with "Bearer "
const getElementByIdPattern = 'document.getElementById';
const getElementByIdPatternRegex =
    /document\.getElementById\(([^)]*)\)\.value\s*=\s*(['"][^'"]*['"])/g;

export enum ScrubType {
    Email = 1 << 0,
    FilePath = 1 << 1,
    SessionId = 1 << 2,
    SearchQuery = 1 << 3,
    GraphqlError = 1 << 4,
    AccountInUrl = 1 << 5,
    RoutingInUrl = 1 << 6,
    AuthToken = 1 << 7,
    DomManipulationErrors = 1 << 8,
}

export function scrubForPii(
    value: string | undefined | null,
    truncate = true,
    scrubType: number = ScrubType.Email |
        ScrubType.FilePath |
        ScrubType.SessionId |
        ScrubType.SearchQuery |
        ScrubType.GraphqlError |
        ScrubType.AccountInUrl |
        ScrubType.AuthToken |
        ScrubType.DomManipulationErrors
): string | undefined {
    if (value) {
        let originalLength = -1;
        let substringLength = -1;
        try {
            originalLength = value.length;
            // If the value is too long, truncate it to a more reasonable length
            if (truncate && originalLength > MAX_CUSTOM_VALUE_LENGTH) {
                value = value.substring(0, MAX_CUSTOM_VALUE_LENGTH);
            }
            substringLength = value.length;

            // We want to scrub for html elements but we don't want to do a regex
            // as it can result in a performance problem
            if (value.indexOf('</div>') > -1 || value.indexOf('</span>') > -1) {
                value = 'DOM present';
            } else {
                if (scrubType & ScrubType.Email) {
                    value = scrubEmailPii(value, originalLength, substringLength);
                }
                if (scrubType & ScrubType.DomManipulationErrors) {
                    value = scrubDomManipulationErrorsPii(value, originalLength, substringLength);
                }
                if (scrubType & ScrubType.SessionId) {
                    value = scrubSessionIdPii(value, originalLength, substringLength);
                }
                if (scrubType & ScrubType.SearchQuery) {
                    value = scrubSearchQuery(value, originalLength, substringLength);
                }
                if (scrubType & ScrubType.FilePath) {
                    value = scrubFileNameForPii(value);
                }
                if (scrubType & ScrubType.AccountInUrl) {
                    value = scrubUrlWithAccountForPii(value);
                }
                if (scrubType & ScrubType.RoutingInUrl) {
                    value = scrubUrlWithRoutingForPii(value, originalLength, substringLength);
                }
                if (scrubType & ScrubType.GraphqlError) {
                    value = sanitizeGraphqlErrorMessage(value);
                }
                if (scrubType & ScrubType.AuthToken) {
                    value = scrubAuthTokens(value);
                }
            }
        } catch (e) {
            value = scrubPiiErrorValue('Unknown', e, originalLength, substringLength);
        }
    }

    return value ?? undefined;
}

function scrubEmailPii(value: string, originalLength: number, substringLength: number) {
    // Try to replace email or replace complete value with error.
    try {
        // The global regex failed in Firefox version 106. We use the non-global regex instead.
        let emailValue = null;
        while ((emailValue = emailRegex.exec(value)) !== null) {
            value = value.replace(emailValue[0], 'EmailPii');
        }
    } catch (e) {
        value = scrubPiiErrorValue('Email', e, originalLength, substringLength);
    }

    return value;
}

function scrubDomManipulationErrorsPii(
    value: string,
    originalLength: number,
    substringLength: number
) {
    // We see that in some cases, username is sometimes logged with the error when getElementById returns null, as part of external script. We are trying to scrub that.
    try {
        if (typeof value === 'string' && value.includes(getElementByIdPattern)) {
            return value.replace(
                getElementByIdPatternRegex,
                "document.getElementById($1).value='UsernamePii'"
            );
        }
    } catch (e) {
        value = scrubPiiErrorValue('Username', e, originalLength, substringLength);
    }
    return value;
}

function scrubSessionIdPii(value: string, originalLength: number, substringLength: number) {
    // Try to replace Session or replace complete value with error.
    try {
        value = value.split(getSessionId()).join('ReplacedSessionId');
    } catch (e) {
        value = scrubPiiErrorValue('SessionId', e, originalLength, substringLength);
    }

    return value;
}

function scrubPiiErrorValue(
    type:
        | 'Unknown'
        | 'Email'
        | 'Username'
        | 'SessionId'
        | 'SearchQuery'
        | 'FilePath'
        | 'Routing'
        | 'AuthToken',
    originalError: unknown,
    originalLength: number,
    substringLength: number
): string {
    const scrubbedMessage = tryScrubErrorValue(getErrorProperty(originalError, 'message'));
    const scrubbedStack = tryScrubErrorValue(getErrorProperty(originalError, 'stack'));

    if (scrubbedMessage === undefined && scrubbedStack === undefined) {
        return `ErrorScrubingPii_${type}. Failed to scrub error details.`;
    }

    return `ErrorScrubingPii_${type}. ${
        substringLength !== MAX_CUSTOM_VALUE_LENGTH &&
        `Original Value Length: ${originalLength}. Substring Value Length: ${substringLength}.`
    } Original Error: ${scrubbedMessage ?? 'Unavailable'}. Callstack: ${
        scrubbedStack ?? 'Unavailable'
    }`;
}

function getErrorProperty(error: unknown, propertyName: 'message' | 'stack'): unknown {
    if (typeof error === 'object' && error !== null && propertyName in error) {
        return (error as Record<'message' | 'stack', unknown>)[propertyName];
    }

    return undefined;
}

function tryScrubErrorValue(value: unknown): string | undefined {
    try {
        const normalizedValue = valueToString(value);
        return scrubForPii(normalizedValue, true)?.substring(0, MAX_CUSTOM_VALUE_LENGTH);
    } catch (_error) {
        try {
            return valueToString(value).substring(0, MAX_CUSTOM_VALUE_LENGTH);
        } catch (_fallbackError) {
            return undefined;
        }
    }
}

function valueToString(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
}

export function scrubFileNameForPii(value: string | undefined): string | undefined {
    if (value) {
        if (value.indexOf(':\\') > -1) {
            const path = value.split('\\');
            path[2] = 'LocalPathPii';
            value = path.join('\\');
        } else if (
            value.indexOf('/home/') == 0 ||
            value.indexOf('/Users/') == 0 ||
            value.indexOf('/Volumes/') == 0
        ) {
            const path = value.split('/');
            path[2] = 'LocalPathPii';
            value = path.join('/');
        }

        if (value.indexOf('/user/') >= 0) {
            value = value.replace(userNameInUrlRegex, 'user/UserPii/');
        }

        if (value.indexOf('?branch=') >= 0) {
            value = value.replace(branchNameRegex, '?branch=UserPii-');
        }
    }

    return value;
}

export function scrubUrlWithAccountForPii(value: string | undefined): string | undefined {
    if (value && (value.includes('/account/') || value.includes('%2Faccount%2F'))) {
        value = value.replace(accountInUrlRegex, '/account/AccountPathPii');
    }

    return value;
}

export function scrubUrlWithRoutingForPii(
    value: string | undefined,
    originalLength: number,
    substringLength: number
): string | undefined {
    try {
        if (value) {
            // The global regex failed in Firefox version 106. We use the non-global regex instead.
            let routingValue = null;
            while ((routingValue = routingInUrlRegex.exec(value)) !== null) {
                value = value.replace(routingValue[1], 'RoutingPii');
            }
        }
    } catch (e) {
        value = scrubPiiErrorValue('Routing', e, originalLength, substringLength);
    }

    return value;
}

export function scrubSearchQuery(
    value: string,
    originalLength: number,
    substringLength: number
): string | undefined {
    try {
        // The global regex failed in Firefox version 106. We use the non-global regex instead.
        let searchQueryValue = null;
        while ((searchQueryValue = searchQueryRegex.exec(value)) !== null) {
            value = value.replace(searchQueryValue[0], searchQueryValue[1] + 'SearchQueryPii');
        }
    } catch (e) {
        value = scrubPiiErrorValue('SearchQuery', e, originalLength, substringLength);
    }

    return value;
}

function scrubAuthTokens(value: string | null | undefined): string | null | undefined {
    value = value?.replace(tokenInUrlRegex, '&AuthTokenPii');

    if (value && (value.includes(encodedTokenStart) || value.includes(bearerTokenStart))) {
        value = 'AuthTokenPii';
    }

    return value;
}
